import {
  toReactive,
  unrefElement,
  useCurrentElement,
  useLocalStorage,
  usePreferredDark,
  type MaybeElementRef,
} from '@vueuse/core';

export type ThemePreference = 'classic' | 'dark' | 'light' | 'system';

// A chart mark's colours: the border, not the fill, is what reaches 3:1
// against the page.
export type ChartColor = { fill: string; border: string };

// How many numbered `--data-color-*` slots tailwind.css defines.
export const CHART_CATEGORICAL_SLOTS = 15;

// Whether the calling component sits inside a `.tw-dark` scope, read from the
// live DOM. Using this is an anti-pattern to avoid wherever possible in favour
// of theme-aware tokens; reach for it only as an escape hatch (e.g. passing the
// user's colour preference to an external service). A renderless root (e.g.
// reka's `Popover.Root`) resolves to a fragment/text node, so fall back to its
// parent element before `.closest`, which can't run on a non-element node.
export function useIsDarkDangerous() {
  const rootEl = useCurrentElement();
  return computed(() => {
    const node = rootEl.value;
    const el = node instanceof Element ? node : (node as Node | undefined)?.parentElement;
    return el?.closest('.tw-dark') != null;
  });
}
export function useThemePreference() {
  // Storage name duplicated in index.html
  const themePreference = useLocalStorage<ThemePreference>('rl-theme-preference', 'system');

  const isPreferredDark = usePreferredDark();

  const theme = computed<Omit<ThemePreference, 'system'>>(() => {
    if (themePreference.value === 'system') {
      return isPreferredDark.value ? 'dark' : 'classic';
    }

    return themePreference.value;
  });

  return { themePreference, theme };
}

/**
 * A simplified, opinionated fork of VueUse's useCssVar, which doesn't mutate
 * CSS variables and cause strange bugs like the VueUse one does.
 */
export function useCssVar(prop: string, target?: MaybeElementRef) {
  const elRef = computed(() => unrefElement(target) || document.body);
  const getValue = () => window.getComputedStyle(elRef.value).getPropertyValue(prop)?.trim();

  const variable = shallowRef(getValue());

  function updateCssVar() {
    variable.value = getValue();
  }
  watchEffect(updateCssVar);

  // This replaces the observe functionality from the original composable
  const { theme } = useThemePreference();
  watch(theme, updateCssVar);

  return variable;
}

/**
 * Helper function for using CSS variables defined in our tailwind config.
 *
 * Returns multiple reactive objects so that if someone tries to destructure
 * the return value, it won't break reactivity
 */
export function useTheme() {
  // The target intentionally isn't configurable and if you're trying to
  // configure it, you're probably trying to do something that you shouldn't be
  const currentEl = useCurrentElement();

  function getScaleForData(colors: [string, string, string, string, string]) {
    return (num: number) => {
      if (num >= 0.95) return colors[4];
      if (num >= 0.85) return colors[3];
      if (num >= 0.7) return colors[2];
      if (num >= 0.5) return colors[1];
      return colors[0];
    };
  }

  const neutral50 = useCssVar('--color-neutral-50', currentEl);
  const neutral200 = useCssVar('--color-neutral-200', currentEl);
  const neutral300 = useCssVar('--color-neutral-300', currentEl);
  const neutral400 = useCssVar('--color-neutral-400', currentEl);
  const neutral500 = useCssVar('--color-neutral-500', currentEl);
  const neutral600 = useCssVar('--color-neutral-600', currentEl);
  const neutral700 = useCssVar('--color-neutral-700', currentEl);
  const neutral800 = useCssVar('--color-neutral-800', currentEl);
  const neutral950 = useCssVar('--color-neutral-950', currentEl);

  const blue300 = useCssVar('--color-blue-300', currentEl);
  const blue500 = useCssVar('--color-blue-500', currentEl);
  const blue700 = useCssVar('--color-blue-700', currentEl);

  const mint300 = useCssVar('--color-mint-300', currentEl);
  const mint400 = useCssVar('--color-mint-400', currentEl);
  const mint500 = useCssVar('--color-mint-500', currentEl);
  const mint600 = useCssVar('--color-mint-600', currentEl);

  const purple300 = useCssVar('--color-purple-300', currentEl);
  const purple500 = useCssVar('--color-purple-500', currentEl);
  const purple700 = useCssVar('--color-purple-700', currentEl);
  const purple800 = useCssVar('--color-purple-800', currentEl);

  const colors = toReactive(
    computed(() => ({
      neutral50: neutral50.value,
      neutral200: neutral200.value,
      neutral300: neutral300.value,
      neutral400: neutral400.value,
      neutral500: neutral500.value,
      neutral600: neutral600.value,
      neutral700: neutral700.value,
      neutral800: neutral800.value,
      neutral950: neutral950.value,
      blue300: blue300.value,
      blue500: blue500.value,
      blue700: blue700.value,
      mint300: mint300.value,
      mint400: mint400.value,
      mint500: mint500.value,
      mint600: mint600.value,
      purple300: purple300.value,
      purple500: purple500.value,
      purple700: purple700.value,
      purple800: purple800.value,
    })),
  );

  const accentPrimary = useCssVar('--accent-color-primary', currentEl);
  const accentPrimaryMuted = useCssVar('--accent-color-primary-muted', currentEl);
  const accentDataHighest = useCssVar('--accent-color-data-highest', currentEl);
  const accentDataHighestMuted = useCssVar('--accent-color-data-highest-muted', currentEl);
  const accentDataHigh = useCssVar('--accent-color-data-high', currentEl);
  const accentDataHighMuted = useCssVar('--accent-color-data-high-muted', currentEl);
  const accentDataMedium = useCssVar('--accent-color-data-medium', currentEl);
  const accentDataMediumMuted = useCssVar('--accent-color-data-medium-muted', currentEl);
  const accentDataLow = useCssVar('--accent-color-data-low', currentEl);
  const accentDataLowMuted = useCssVar('--accent-color-data-low-muted', currentEl);
  const accentDataLowest = useCssVar('--accent-color-data-lowest', currentEl);
  const accentDataLowestMuted = useCssVar('--accent-color-data-lowest-muted', currentEl);
  const accentDataInfo = useCssVar('--accent-color-data-info', currentEl);
  const accentDataInfoMuted = useCssVar('--accent-color-data-info-muted', currentEl);

  const accent = toReactive(
    computed(() => ({
      primary: accentPrimary.value,
      primaryMuted: accentPrimaryMuted.value,
      dataScale: getScaleForData([
        accentDataHighest.value,
        accentDataHigh.value,
        accentDataMedium.value,
        accentDataLow.value,
        accentDataLowest.value,
      ]),
      dataScaleMuted: getScaleForData([
        accentDataHighestMuted.value,
        accentDataHighMuted.value,
        accentDataMediumMuted.value,
        accentDataLowMuted.value,
        accentDataLowestMuted.value,
      ]),
      dataInfo: accentDataInfo.value,
      dataInfoMuted: accentDataInfoMuted.value,
    })),
  );

  const borderNeutral = useCssVar('--border-color-neutral', currentEl);
  const borderNeutralMuted = useCssVar('--border-color-neutral-muted', currentEl);
  const borderDataHighest = useCssVar('--border-color-data-highest', currentEl);
  const borderDataHighestMuted = useCssVar('--border-color-data-highest-muted', currentEl);
  const borderDataHigh = useCssVar('--border-color-data-high', currentEl);
  const borderDataHighMuted = useCssVar('--border-color-data-high-muted', currentEl);
  const borderDataMedium = useCssVar('--border-color-data-medium', currentEl);
  const borderDataMediumMuted = useCssVar('--border-color-data-medium-muted', currentEl);
  const borderDataLow = useCssVar('--border-color-data-low', currentEl);
  const borderDataLowMuted = useCssVar('--border-color-data-low-muted', currentEl);
  const borderDataLowest = useCssVar('--border-color-data-lowest', currentEl);
  const borderDataLowestMuted = useCssVar('--border-color-data-lowest-muted', currentEl);
  const borderDataInfo = useCssVar('--border-color-data-info', currentEl);
  const borderDataInfoMuted = useCssVar('--border-color-data-info-muted', currentEl);

  const border = toReactive(
    computed(() => ({
      neutral: borderNeutral.value,
      neutralMuted: borderNeutralMuted.value,
      dataScale: getScaleForData([
        borderDataHighest.value,
        borderDataHigh.value,
        borderDataMedium.value,
        borderDataLow.value,
        borderDataLowest.value,
      ]),
      dataScaleMuted: getScaleForData([
        borderDataHighestMuted.value,
        borderDataHighMuted.value,
        borderDataMediumMuted.value,
        borderDataLowMuted.value,
        borderDataLowestMuted.value,
      ]),
      dataInfo: borderDataInfo.value,
      dataInfoMuted: borderDataInfoMuted.value,
    })),
  );

  const bgDefault = useCssVar('--background-color-default', currentEl);
  const bgNeutral = useCssVar('--background-color-neutral', currentEl);
  const bgNeutralActive = useCssVar('--background-color-neutral-active', currentEl);
  const bgStrongNeutral = useCssVar('--background-color-strong-neutral', currentEl);
  const bgInfo = useCssVar('--background-color-info', currentEl);
  const bgStrongDataHighest = useCssVar('--background-color-strong-data-highest', currentEl);
  const bgStrongDataHigh = useCssVar('--background-color-strong-data-high', currentEl);
  const bgStrongDataMedium = useCssVar('--background-color-strong-data-medium', currentEl);
  const bgStrongDataLow = useCssVar('--background-color-strong-data-low', currentEl);
  const bgStrongDataLowest = useCssVar('--background-color-strong-data-lowest', currentEl);

  const bg = toReactive(
    computed(() => ({
      default: bgDefault.value,
      neutral: bgNeutral.value,
      neutralActive: bgNeutralActive.value,
      strongNeutral: bgStrongNeutral.value,
      info: bgInfo.value,
      strongDataScale: getScaleForData([
        bgStrongDataHighest.value,
        bgStrongDataHigh.value,
        bgStrongDataMedium.value,
        bgStrongDataLow.value,
        bgStrongDataLowest.value,
      ]),
    })),
  );

  const textNeutral = useCssVar('--text-color-neutral', currentEl);
  const textNeutralMuted = useCssVar('--text-color-neutral-muted', currentEl);
  const textNeutralSubtle = useCssVar('--text-color-neutral-subtle', currentEl);
  const textDataHighest = useCssVar('--text-color-data-highest', currentEl);
  const textDataHigh = useCssVar('--text-color-data-high', currentEl);
  const textDataMedium = useCssVar('--text-color-data-medium', currentEl);
  const textDataLow = useCssVar('--text-color-data-low', currentEl);
  const textDataLowest = useCssVar('--text-color-data-lowest', currentEl);

  const text = toReactive(
    computed(() => ({
      neutral: textNeutral.value,
      neutralMuted: textNeutralMuted.value,
      neutralSubtle: textNeutralSubtle.value,
      dataScale: getScaleForData([
        textDataHighest.value,
        textDataHigh.value,
        textDataMedium.value,
        textDataLow.value,
        textDataLowest.value,
      ]),
    })),
  );

  const fontSans = useCssVar('--font-sans', currentEl);
  const fontMono = useCssVar('--font-mono', currentEl);

  const font = toReactive(
    computed(() => ({
      sans: fontSans.value,
      mono: fontMono.value,
    })),
  );

  const networkConnectorDefault = useCssVar('--network-color-connector-default', currentEl);
  const networkConnectorSelected = useCssVar('--network-color-connector-selected', currentEl);
  const networkConnectorSecondary = useCssVar('--network-color-connector-secondary', currentEl);

  const networkNodeOrganisation = useCssVar('--network-color-node-organisation', currentEl);

  const networkNodeThirdPartyDefault = useCssVar(
    '--network-color-node-third-party-default',
    currentEl,
  );
  const networkNodeThirdPartySelected = useCssVar(
    '--network-color-node-third-party-selected',
    currentEl,
  );
  const networkNodeThirdPartyUnselected = useCssVar(
    '--network-color-node-third-party-unselected',
    currentEl,
  );

  const networkNodeFourthPartyDefault = useCssVar(
    '--network-color-node-fourth-party-default',
    currentEl,
  );
  const networkNodeFourthPartySelected = useCssVar(
    '--network-color-node-fourth-party-selected',
    currentEl,
  );
  const networkNodeFourthPartyUnselected = useCssVar(
    '--network-color-node-fourth-party-unselected',
    currentEl,
  );

  const networkNodeConcentrationRiskDefault = useCssVar(
    '--network-color-node-concentration-risk-default',
    currentEl,
  );
  const networkNodeConcentrationRiskUnselected = useCssVar(
    '--network-color-node-concentration-risk-unselected',
    currentEl,
  );

  const networkNodeCommunityDefault = useCssVar(
    '--network-color-node-community-default',
    currentEl,
  );
  const networkNodeCommunityUnselected = useCssVar(
    '--network-color-node-community-unselected',
    currentEl,
  );

  const networkNodeAssessed0 = useCssVar('--network-color-node-assessed-0', currentEl);
  const networkNodeAssessed50 = useCssVar('--network-color-node-assessed-50', currentEl);
  const networkNodeAssessed100 = useCssVar('--network-color-node-assessed-100', currentEl);

  const network = toReactive(
    computed(() => ({
      connector: {
        default: networkConnectorDefault.value,
        selected: networkConnectorSelected.value,
        secondary: networkConnectorSecondary.value,
      },
      node: {
        organisation: networkNodeOrganisation.value,
        thirdParty: {
          default: networkNodeThirdPartyDefault.value,
          selected: networkNodeThirdPartySelected.value,
          unselected: networkNodeThirdPartyUnselected.value,
        },
        fourthParty: {
          default: networkNodeFourthPartyDefault.value,
          selected: networkNodeFourthPartySelected.value,
          unselected: networkNodeFourthPartyUnselected.value,
        },
        concentrationRisk: {
          default: networkNodeConcentrationRiskDefault.value,
          unselected: networkNodeConcentrationRiskUnselected.value,
        },
        community: {
          default: networkNodeCommunityDefault.value,
          unselected: networkNodeCommunityUnselected.value,
        },
        // Three-stop gradient (0%/50%/100% compliance) — consumed via a
        // chroma.scale(...).domain([0, 100]) by callers, same as before.
        assessed: [
          networkNodeAssessed0.value,
          networkNodeAssessed50.value,
          networkNodeAssessed100.value,
        ] as [string, string, string],
      },
    })),
  );

  const isDarkDangerous = useIsDarkDangerous();

  return { colors, accent, border, bg, text, font, network, isDarkDangerous };
}

/**
 * The categorical chart colors. They're separate from useTheme, which mounts
 * in components that render many times over, such as BaseProgressBar, and has
 * no use for them. Each slot is a fill and a border, in the order they're
 * given out, so a chart with n categories takes the first n.
 */
export function useChartTheme() {
  const currentEl = useCurrentElement();

  function useChartColor(name: string) {
    return {
      fill: useCssVar(`--data-color-${name}`, currentEl),
      border: useCssVar(`--data-color-border-${name}`, currentEl),
    };
  }

  const categorical = Array.from({ length: CHART_CATEGORICAL_SLOTS }, (_, i) =>
    useChartColor(`${i + 1}`),
  );
  const other = useChartColor('other');
  const number = useCssVar('--chart-number', currentEl);

  const read = (color: ReturnType<typeof useChartColor>): ChartColor => ({
    fill: color.fill.value,
    border: color.border.value,
  });

  return computed(() => ({
    categorical: categorical.map(read),
    other: read(other),
    number: number.value,
  }));
}
